import React from 'react';

/**
 * MobileDaySelector - 7-day grid selector for mobile
 *
 * Props:
 * - currentWeekStart: Date - Start of the current week (Monday)
 * - selectedDate: Date - Currently selected date
 * - slotsMap: Object - Map of date strings to slot arrays { 'YYYY-MM-DD': [...slots] }
 * - onDateSelect: (date: Date) => void - Callback when date is selected
 */
const MobileDaySelector = ({ currentWeekStart, selectedDate, slotsMap, onDateSelect }) => {
  // Generate array of 7 days starting from currentWeekStart
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + i);
    return date;
  });

  // Format date to YYYY-MM-DD for slot lookup
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if two dates are the same day
  const isSameDay = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  // Day abbreviations
  const dayAbbreviations = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((date, index) => {
          const dateKey = formatDateKey(date);
          const hasSlots = slotsMap[dateKey] && slotsMap[dateKey].length > 0;
          const isSelected = isSameDay(date, selectedDate);
          const dayNum = date.getDate();
          const dayAbbr = dayAbbreviations[index];

          return (
            <button
              key={dateKey}
              onClick={() => onDateSelect(date)}
              className={`
                relative
                flex flex-col items-center justify-center
                h-16 rounded-lg
                transition-all duration-150
                active:scale-95
                touch-manipulation
                ${isSelected
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                }
              `}
              aria-label={`${dayAbbr}, ${dayNum}`}
            >
              {/* Day abbreviation */}
              <div className={`
                text-xs font-medium mb-1
                ${isSelected ? 'text-white' : 'text-gray-600 dark:text-gray-400'}
              `}>
                {dayAbbr}
              </div>

              {/* Date number */}
              <div className="text-lg font-semibold">
                {dayNum}
              </div>

              {/* Green indicator bar if slots exist */}
              {hasSlots && (
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                  <div className={`
                    w-6 h-1 rounded-full
                    ${isSelected ? 'bg-white' : 'bg-green-500'}
                  `} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MobileDaySelector;
